import { defineStore } from 'pinia';
import type { JobApplicant, JobApplicantStatusHistory } from '@factoryfixinc/ats-interfaces';
import { ref } from 'vue';
import type { ProUserProfileWithRelations } from './pro-profile';
import type { JobApplicantWithJobTitle } from '@/core/shared/job/job.type';
import type { UserMessageStatus } from '../message/types/get-user-message-status.type';
import type { DoNotContactSummary } from '../do-not-contact/do-not-contact.type';

export const useConversationProProfileStore = defineStore(
  'conversationProProfile',
  () => {
    const selectedPro = ref<ProUserProfileWithRelations | null>();
    const selectedProEmployerId = ref<number>();
    const selectedProApplication = ref<JobApplicantWithJobTitle>();
    const selectedProMessageStatus = ref<UserMessageStatus>();
    const messageStatusRequestId = ref(0);
    const messageStatusRefreshState = ref<'idle' | 'loading' | 'error'>('idle');
    const requestedEmployerId = ref<number>();
    const profileRequestId = ref(0);
    const selectedProJobApplicantStatusHistory = ref<JobApplicantStatusHistory[]>([]);
    // Tracks the most recently requested user-profile id so late responses for an
    // older request can be ignored. See ONE-5844: without this guard, a slow
    // profile fetch for candidate A overwrites candidate B's data after the user
    // has already moved on.
    const requestedProId = ref<number | null>(null);
    // The request generation the panel is waiting on, not a boolean: only the
    // request that is still current may take the skeleton down. Armed by a
    // candidate selection only, so a background refresh never blanks the pane.
    const loadingProfileRequestId = ref<number | null>(null);
    // The candidate whose profile failed, so the error state follows the
    // clicked candidate even though `clearSelectedPro` bumps the request id.
    const failedProfileProId = ref<number | null>(null);

    function requestPro(
      userProfileId: number,
      employerId?: number,
      opts: { isSelection?: boolean } = {},
    ) {
      if (requestedProId.value !== userProfileId || requestedEmployerId.value !== employerId) {
        clearSelectedProMessageStatus();
      }
      requestedProId.value = userProfileId;
      requestedEmployerId.value = employerId;
      const requestId = ++profileRequestId.value;
      // Only the click that changes who the panel is about may take the panel
      // over; a background refresh still bumps the generation for staleness.
      if (opts.isSelection) {
        failedProfileProId.value = null;
        loadingProfileRequestId.value = requestId;
      }
      return requestId;
    }

    function settleProfileRequest(requestId: number, failedProId: number | null = null) {
      if (profileRequestId.value !== requestId) return;
      loadingProfileRequestId.value = null;
      failedProfileProId.value = failedProId;
    }

    function clearSelectedProMessageStatus() {
      ++messageStatusRequestId.value;
      selectedProMessageStatus.value = undefined;
      messageStatusRefreshState.value = 'idle';
    }

    function setSelectedPro(pro: ProUserProfileWithRelations) {
      if (requestedProId.value !== null && pro.id !== requestedProId.value) return;
      selectedPro.value = pro;
      selectedProEmployerId.value = requestedEmployerId.value;
    }
    function clearSelectedPro() {
      selectedPro.value = null;
      selectedProEmployerId.value = undefined;
      ++profileRequestId.value;
      // The bump orphans any in-flight request, so nothing is left to settle it.
      loadingProfileRequestId.value = null;
    }
    /** No candidate owns the panel any more: nothing is pending and nothing failed. */
    function abandonProfileRequest() {
      loadingProfileRequestId.value = null;
      failedProfileProId.value = null;
    }
    function clearSelectedProApplication() {
      selectedProApplication.value = undefined;
    }
    function selectJobApplication(id: JobApplicant['id']) {
      selectedProApplication.value =
        selectedPro.value?.jobApplicants?.find((a: JobApplicantWithJobTitle) => a.id == id) ||
        undefined;
    }

    function setSelectedProMessageStatus(status: UserMessageStatus) {
      selectedProMessageStatus.value = status;
    }

    // Guarded by user-profile id: the DNC request can outlive the selection, and
    // a late completion must never flag the candidate the user navigated to.
    function setSelectedProDoNotContact(
      userProfileId: number,
      doNotContact: DoNotContactSummary | null,
    ) {
      if (!selectedPro.value || selectedPro.value.id !== userProfileId) return;
      ++profileRequestId.value;
      selectedPro.value.doNotContact = doNotContact;
    }

    function patchApplicationStatus(
      applicationId: JobApplicant['id'],
      newJobApplicant: JobApplicant,
    ) {
      if (selectedPro.value) {
        const { jobApplicants } = selectedPro.value;
        const application = jobApplicants?.find(
          (app: JobApplicantWithJobTitle) => app.id === applicationId,
        );
        if (application) {
          application.status = newJobApplicant.status;
          application.rejectionReason = newJobApplicant.rejectionReason;
          application.updateTs = newJobApplicant.updateTs;
        }
      }
    }
    // Local mirror of a backend cascade (e.g. the Do Not Contact reject) for
    // when the authoritative refetch is unavailable.
    function patchApplicationStatuses(
      applicationIds: JobApplicant['id'][],
      patch: Pick<JobApplicant, 'status' | 'rejectionReason'>,
    ) {
      const ids = new Set(applicationIds);
      selectedPro.value?.jobApplicants?.forEach((application: JobApplicantWithJobTitle) => {
        if (!ids.has(application.id)) return;
        application.status = patch.status;
        application.rejectionReason = patch.rejectionReason;
      });
    }

    // alphabetized list
    return {
      abandonProfileRequest,
      clearSelectedPro,
      clearSelectedProApplication,
      clearSelectedProMessageStatus,
      failedProfileProId,
      loadingProfileRequestId,
      messageStatusRefreshState,
      messageStatusRequestId,
      patchApplicationStatus,
      patchApplicationStatuses,
      profileRequestId,
      requestPro,
      requestedEmployerId,
      requestedProId,
      selectJobApplication,
      selectedPro,
      selectedProApplication,
      selectedProEmployerId,
      selectedProJobApplicantStatusHistory,
      selectedProMessageStatus,
      setSelectedPro,
      setSelectedProDoNotContact,
      setSelectedProMessageStatus,
      settleProfileRequest,
    };
  },
  {
    persist: false,
  },
);
